import { useCallback, useEffect, useState } from "react";

export function useLocalStorage<T>(
  key: string,
  initialValue: T,
): [T, (value: T | ((prevValue: T) => T)) => void] {
  // Get from local storage then parse stored json or return initialValue
  const readValue = (): T => {
    // Prevent build error "window is undefined" but keep working
    if (typeof window === "undefined") {
      return initialValue;
    }

    try {
      const item = window.localStorage.getItem(key);
      return item ? (JSON.parse(item) as T) : initialValue;
    } catch (error) {
      return initialValue;
    }
  };

  // State to store our value
  // Pass initial state function to useState so logic is only executed once
  const [storedValue, setStoredValue] = useState<T>(readValue);

  // Return a wrapped version of useState's setter function that ...
  // ... persists the new value to localStorage.
  const setValue = useCallback(
    (value: T | ((prevValue: T) => T)) => {
      // Prevent build error "window is undefined" but keeps working
      if (typeof window === "undefined") {
        return;
      }

      try {
        // Use functional update to ensure we always have the latest value
        setStoredValue((currentStoredValue) => {
          const newValue =
            value instanceof Function ? value(currentStoredValue) : value;

          // Save to local storage
          window.localStorage.setItem(key, JSON.stringify(newValue));

          return newValue;
        });
      } catch (error) {
        // Error setting localStorage
      }
    },
    [key],
  );

  useEffect(() => {
    setStoredValue(readValue());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return [storedValue, setValue];
}
